import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Expand, Sparkles, X } from 'lucide-react';
import { useApp } from '../../data/AppProvider';
import { keys, useTrip } from '../../data/hooks';
import { ElioChat } from './ElioChat';
import type { ChatAttachment } from '../../domain/assistant';

interface DrawerApi { open: (opts?: { tripId?: string | null; text?: string; files?: ChatAttachment[] }) => void; close: () => void; isOpen: boolean }
const Ctx = createContext<DrawerApi>({ open: () => {}, close: () => {}, isOpen: false });
export const useElioDrawer = () => useContext(Ctx);

/** Trip in view, from the URL (e.g. /trips/:id/...). */
function useTripInView(): string | null {
  const loc = useLocation();
  return /^\/trips\/([0-9a-f-]{36}|demo-[\w-]+)/.exec(loc.pathname)?.[1] ?? null;
}

/**
 * Elio beside any page: a side panel (full screen on phones) holding the conversation for the
 * trip being viewed, so questions can be asked without leaving the page.
 */
export function ElioDrawerProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ open: boolean; tripId: string | null; text: string | null; files: ChatAttachment[] | null; nonce: number }>({ open: false, tripId: null, text: null, files: null, nonce: 0 });
  const inView = useTripInView();
  const loc = useLocation();
  const open = useCallback((opts?: { tripId?: string | null; text?: string; files?: ChatAttachment[] }) => setState((s) => ({ open: true, tripId: opts?.tripId ?? inView, text: opts?.text ?? null, files: opts?.files ?? null, nonce: s.nonce + 1 })), [inView]);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  useEffect(() => { if (loc.pathname === '/' || loc.pathname.startsWith('/c/')) close(); }, [loc.pathname, close]);
  const api = useMemo(() => ({ open, close, isOpen: state.open }), [open, close, state.open]);
  return (
    <Ctx.Provider value={api}>
      {children}
      {state.open && <Drawer key={state.nonce} tripId={state.tripId} initialText={state.text} initialFiles={state.files} onClose={close} />}
    </Ctx.Provider>
  );
}

function Drawer({ tripId, initialText, initialFiles, onClose }: { tripId: string | null; initialText: string | null; initialFiles: ChatAttachment[] | null; onClose: () => void }) {
  const { data, session } = useApp();
  const trip = useTrip(tripId);
  const conversations = useQuery({ queryKey: keys.conversations, queryFn: () => data.listConversations(), enabled: Boolean(session) && data.kind !== 'demo' });
  const latest = tripId ? conversations.data?.find((c) => c.trip_id === tripId)?.id ?? null : null;
  const [conversationId, setConversationId] = useState<string | null>(null);
  const convId = conversationId ?? latest;
  const ready = !tripId || conversations.isFetched || data.kind === 'demo';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const title = trip.data?.trip.title ?? null;
  const suggestions = title
    ? ['What’s next?', 'Check for clashes', 'Plan the next journey', 'Ideas for tomorrow']
    : ['Help me plan a trip', 'Where could I go next?', 'Which documents do I need?'];

  return (
    <aside role="dialog" aria-modal="false" aria-label="Elio" className="fade-in fixed inset-0 z-50 flex flex-col bg-ivory shadow-[var(--shadow-lift)] sm:inset-y-3 sm:left-auto sm:right-3 sm:w-[27.5rem] sm:max-w-[calc(100vw-1.5rem)] sm:rounded-3xl sm:border sm:border-line">
      <div className="flex items-center gap-2 border-b border-line px-4 py-3">
        <img src="/icon.svg" alt="" className="size-8 rounded-full" />
        <div className="min-w-0 flex-1">
          <p className="font-display text-base leading-tight">Elio</p>
          <p className="truncate text-xs text-muted">{title ? `About ${title}` : 'Your travel companion'}</p>
        </div>
        <Link to={convId ? `/c/${convId}` : '/'} className="inline-flex size-10 items-center justify-center rounded-full text-muted hover:bg-white hover:text-ink" aria-label="Open full conversation" title="Open full conversation"><Expand className="size-4" /></Link>
        <button type="button" onClick={onClose} className="inline-flex size-10 items-center justify-center rounded-full text-muted hover:bg-white hover:text-ink" aria-label="Close Elio"><X className="size-5" /></button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col px-3 pb-3">
        {ready ? (
          <ElioChat key={convId ?? `new-${tripId ?? 'none'}`} conversationId={convId} tripId={tripId} tripTitle={title} compact
            onConversationCreated={setConversationId} suggestions={suggestions} initialText={initialText} initialFiles={initialFiles} />
        ) : <p className="p-6 text-sm text-muted">Opening…</p>}
      </div>
    </aside>
  );
}

export function AskElioButton() {
  const { open, isOpen } = useElioDrawer();
  const loc = useLocation();
  if (isOpen || loc.pathname === '/' || loc.pathname.startsWith('/c/')) return null;
  return (
    <button type="button" onClick={() => open()} aria-label="Ask Elio"
      className="fixed bottom-20 right-4 z-30 inline-flex min-h-12 items-center gap-2 rounded-full bg-gradient-to-r from-forest-700 to-turquoise-700 px-4 text-sm font-semibold text-white shadow-[var(--shadow-lift)] transition hover:brightness-110 lg:bottom-6 lg:right-6 lg:px-5">
      <Sparkles className="size-5" aria-hidden /><span className="hidden sm:inline">Ask Elio</span>
    </button>
  );
}
